import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';
import './Legal.css';

const Cookies: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <div className="legal-title-section">
            <Cookie size={48} className="legal-icon" />
            <h1>Cookie Policy</h1>
            <p className="legal-subtitle">Last updated: January 2024</p>
          </div>
        </div>
      </div>

      <div className="legal-content">
        <div className="container">
          <div className="legal-body">
            <section>
              <h2>1. What Are Cookies?</h2>
              <p>
                Cookies are small text files stored on your device (computer, tablet, or mobile) 
                when you visit our website. They help us provide a better user experience by 
                remembering your preferences and activities.
              </p>
            </section>

            <section>
              <h2>2. How We Use Cookies</h2>
              <p>We use cookies for the following purposes:</p>
              <ul>
                <li><strong>Authentication:</strong> To keep you logged in to your account</li>
                <li><strong>Security:</strong> To detect and prevent fraud and abuse</li>
                <li><strong>Preferences:</strong> To remember your settings and choices</li>
                <li><strong>Analytics:</strong> To understand how you use our service</li>
                <li><strong>Performance:</strong> To optimize website speed and functionality</li>
              </ul>
            </section>

            <section>
              <h2>3. Types of Cookies We Use</h2>
              
              <h3>3.1 Essential Cookies</h3>
              <p>These cookies are necessary for the website to function properly:</p>
              <div className="cookie-table">
                <table>
                  <thead>
                    <tr>
                      <th>Cookie Name</th>
                      <th>Purpose</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>auth_token</code></td>
                      <td>Maintains your login session</td>
                      <td>7 days</td>
                    </tr>
                    <tr>
                      <td><code>session_id</code></td>
                      <td>Identifies your browsing session</td>
                      <td>Session</td>
                    </tr>
                    <tr>
                      <td><code>csrf_token</code></td>
                      <td>Security protection against CSRF attacks</td>
                      <td>Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3>3.2 Functional Cookies</h3>
              <p>These cookies enhance functionality and personalization:</p>
              <div className="cookie-table">
                <table>
                  <thead>
                    <tr>
                      <th>Cookie Name</th>
                      <th>Purpose</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>user_preferences</code></td>
                      <td>Stores your dashboard settings</td>
                      <td>1 year</td>
                    </tr>
                    <tr>
                      <td><code>theme</code></td>
                      <td>Remembers your theme preference</td>
                      <td>1 year</td>
                    </tr>
                    <tr>
                      <td><code>language</code></td>
                      <td>Stores your language preference</td>
                      <td>1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3>3.3 Analytics Cookies</h3>
              <p>These cookies help us understand how you interact with our service:</p>
              <div className="cookie-table">
                <table>
                  <thead>
                    <tr>
                      <th>Cookie Name</th>
                      <th>Purpose</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>_ga</code></td>
                      <td>Google Analytics - tracks user behavior</td>
                      <td>2 years</td>
                    </tr>
                    <tr>
                      <td><code>_gid</code></td>
                      <td>Google Analytics - distinguishes users</td>
                      <td>24 hours</td>
                    </tr>
                    <tr>
                      <td><code>analytics_session</code></td>
                      <td>Tracks page views and interactions</td>
                      <td>30 minutes</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3>3.4 Performance Cookies</h3>
              <p>These cookies help us optimize service performance:</p>
              <div className="cookie-table">
                <table>
                  <thead>
                    <tr>
                      <th>Cookie Name</th>
                      <th>Purpose</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>performance_id</code></td>
                      <td>Measures page load times</td>
                      <td>Session</td>
                    </tr>
                    <tr>
                      <td><code>error_tracking</code></td>
                      <td>Logs errors for debugging</td>
                      <td>7 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2>4. Third-Party Cookies</h2>
              <p>We use third-party services that may set cookies:</p>
              <ul>
                <li><strong>Google Analytics:</strong> For website analytics</li>
                <li><strong>Payment Processors:</strong> For secure payment processing</li>
                <li><strong>CDN Providers:</strong> For content delivery</li>
              </ul>
              <p>
                These third parties have their own privacy policies. 
                We recommend reviewing them:
              </p>
              <ul>
                <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
              </ul>
            </section>

            <section>
              <h2>5. Managing Cookies</h2>
              
              <h3>5.1 Browser Settings</h3>
              <p>You can control cookies through your browser settings:</p>
              <ul>
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                <li><strong>Edge:</strong> Settings → Privacy → Cookies</li>
              </ul>

              <h3>5.2 Opt-Out Options</h3>
              <p>To opt out of analytics cookies:</p>
              <ul>
                <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
              </ul>

              <h3>5.3 Impact of Disabling Cookies</h3>
              <p>If you disable cookies, some features may not work properly:</p>
              <ul>
                <li>You may need to log in each time</li>
                <li>Your preferences won't be saved</li>
                <li>Some features may be unavailable</li>
              </ul>
              <p><strong>Note:</strong> Essential cookies cannot be disabled as they are required for the service to function.</p>
            </section>

            <section>
              <h2>6. Local Storage and Similar Technologies</h2>
              <p>In addition to cookies, we may use:</p>
              <ul>
                <li><strong>Local Storage:</strong> To store larger amounts of data locally</li>
                <li><strong>Session Storage:</strong> For temporary data during your session</li>
                <li><strong>IndexedDB:</strong> For structured data storage</li>
              </ul>
              <p>These technologies serve similar purposes to cookies but can store more data.</p>
            </section>

            <section>
              <h2>7. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices 
                or for legal, operational, or regulatory reasons.
              </p>
              <p>
                The "Last updated" date at the top of this policy indicates when it was last revised.
              </p>
            </section>

            <section>
              <h2>8. Contact Us</h2>
              <p>If you have questions about our use of cookies:</p>
              <ul>
                <li>Email: <a href="mailto:privacy@flazu.com">privacy@flazu.com</a></li>
                <li>Support: <a href="mailto:support@flazu.com">support@flazu.com</a></li>
              </ul>
            </section>

            <section>
              <h2>9. Related Policies</h2>
              <p>For more information about how we handle your data:</p>
              <ul>
                <li><Link to="/privacy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cookies;